import { useCallback, useRef, useState } from 'react';
import {
  ApiErrorResponse,
  confirmationHeaders,
  useEditSessionContext,
  useEditSessionFormRowsContext,
  useInputDataDetailsPreviewMutation,
} from '@atlas-frontend/sdk';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import {
  translateMutationError,
  useInputDataActions,
} from '../details/useInputDataActions';
import { confirmInputDataWarnings } from '../details/confirmInputDataWarnings';
import translations from './useCalculatePreview.translations.json';

const { addTranslations, t } = i18nNsFactory('input-data-calculate-preview');
addTranslations(translations);

export function useCalculatePreview(onSuccess?: () => void) {
  const { sessionId, refreshRows, flushEdits } = useEditSessionContext();
  const { preparePendingRow } = useEditSessionFormRowsContext();
  const { onClear, onError } = useInputDataActions();
  const { mutateAsync: mutateInputDataPreviewAsync } =
    useInputDataDetailsPreviewMutation();
  const [isCalculating, setIsCalculating] = useState(false);
  // Ref guard, the state is not updated yet when clicked twice in quick succession
  const isCalculatingRef = useRef(false);

  const calculatePreview = useCallback(
    async (confirm?: boolean) => {
      if (sessionId == null || isCalculatingRef.current) return;
      isCalculatingRef.current = true;
      setIsCalculating(true);
      try {
        // Calculate with the pending row inserted and all edits sent
        if ((await preparePendingRow()) === 'blocked') {
          return;
        }
        await flushEdits();
        onClear();
        await mutateInputDataPreviewAsync({
          data: { sessionId },
          headers: confirmationHeaders(confirm),
        });
        refreshRows();
        onSuccess?.();
      } catch (error) {
        const apiErrorResponse = error as ApiErrorResponse;
        if (
          !confirm &&
          confirmInputDataWarnings(apiErrorResponse, translateMutationError, {
            title: t('confirm.title'),
            okText: t('confirm.okText'),
            cancelText: t('confirm.cancelText'),
            onOk: () => calculatePreview(true),
            onCancel: onClear,
          })
        ) {
          return;
        }
        onError(apiErrorResponse);
      } finally {
        isCalculatingRef.current = false;
        setIsCalculating(false);
      }
    },
    [
      mutateInputDataPreviewAsync,
      sessionId,
      preparePendingRow,
      flushEdits,
      refreshRows,
      onClear,
      onError,
      onSuccess,
    ],
  );

  return { calculatePreview, isCalculating };
}
